import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArchiveIcon, CoinsIcon, ShapesIcon, TargetIcon, TriangleAlertIcon, WalletIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { poolLabel, usePools } from '@/api/pools'
import type { Pool, PoolKind } from '@/api/pools'
import { useWorkspace } from '@/api/workspace'
import { FilterBar } from '@/components/filter-bar'
import { LoadingSkeleton } from '@/components/loading-skeleton'
import { MaterialsActions } from '@/components/materials/materials-actions'
import { PoolFlags } from '@/components/materials/pool-flags'
import { formatCostPerUnit, formatMoney, formatQty } from '@/components/materials/pool-format'
import { RecordLink, RecordList, RecordListEmpty, RecordListItem } from '@/components/record-list'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'

// Pools list (UI-SCREENS M-01, #64): search beside the shared filter bar (as on I-03) for kind
// (Filament / Component; both when unset), condition and Archived. The filters live in the URL.
// Restock and Adjustment arrive with 02c (#66) and 02d (#67).
//
// `?attention=true` lists only the non-archived Pools that need attention — "needs stock count" or
// below minimum: the dashboard's "see all" (#69) opens it.
const KINDS = ['filament', 'component'] as const satisfies readonly PoolKind[]

const searchSchema = z.object({
  kind: z.enum(KINDS).optional().catch(undefined),
  attention: z.boolean().optional().catch(undefined),
  archived: z.boolean().optional().catch(undefined),
})

export const Route = createFileRoute('/w/$slug/materials/')({
  component: MaterialsList,
  validateSearch: searchSchema,
  staticData: { breadcrumb: 'materials.title' },
})

function MaterialsList() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const { kind, attention = false, archived = false } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const workspace = useWorkspace(slug)
  const [search, setSearch] = useState('')
  const pools = usePools(slug, { archived })

  const shown = useMemo(() => filterPools(pools.data, kind, search, attention), [pools.data, kind, search, attention])

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-xl font-semibold">{t('materials.title')}</h1>
        <div className="flex flex-wrap gap-2">
          <MaterialsActions slug={slug} />
          {workspace.data?.role === 'owner' ? (
            <Button asChild className="h-11">
              <Link to="/w/$slug/materials/pools/new" params={{ slug }}>
                {t('materials.newPool')}
              </Link>
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          type="search"
          className="h-9 sm:max-w-xs"
          aria-label={t('materials.search')}
          placeholder={t('materials.search')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <FilterBar
          fields={[
            {
              key: 'kind',
              label: t('materials.filterKind'),
              icon: ShapesIcon,
              value: kind,
              options: KINDS.map((value) => ({ value, label: t(`materials.${value}`) })),
              onChange: (value) =>
                navigate({ search: (prev) => ({ ...prev, kind: value as PoolKind | undefined }), replace: true }),
            },
            {
              key: 'attention',
              label: t('materials.filterCondition'),
              icon: TriangleAlertIcon,
              value: attention ? 'true' : undefined,
              options: [{ value: 'true', label: t('materials.needsAttention') }],
              onChange: (value) =>
                navigate({ search: (prev) => ({ ...prev, attention: value === 'true' ? true : undefined }), replace: true }),
            },
            {
              key: 'archived',
              label: t('filterBar.archived'),
              icon: ArchiveIcon,
              value: archived ? 'true' : undefined,
              options: [{ value: 'true', label: t('filterBar.shown') }],
              onChange: (value) =>
                navigate({ search: (prev) => ({ ...prev, archived: value === 'true' ? true : undefined }), replace: true }),
            },
          ]}
          onClearAll={() => navigate({ search: {}, replace: true })}
        />
      </div>

      <FirstUseHint
        slug={slug}
        pools={pools.data}
        isOwner={workspace.data?.role === 'owner'}
        fromScratch={workspace.data?.onboarding.onboarding_track === 'from_scratch'}
      />

      {pools.isPending ? (
        <LoadingSkeleton />
      ) : pools.isError || !pools.data ? (
        <p role="alert" className="text-sm text-destructive">
          {t('errors.generic')}
        </p>
      ) : shown.length === 0 ? (
        <RecordListEmpty>{t('materials.empty')}</RecordListEmpty>
      ) : (
        <RecordList>
          {shown.map((pool) => (
            <PoolRow key={pool.id} slug={slug} pool={pool} />
          ))}
        </RecordList>
      )}
    </section>
  )
}

// First-use state (UI-SCREENS §2 "Empty states"): Pools exist but nothing has stock yet, so the
// Owner is pointed at the opening balance. On the from-scratch track there is no stock to carry
// over, so it points at creating a Pool and recording a Restock instead, and shows before the
// first Pool too (#262). Staff never see it — I-13 is Owner only.
function FirstUseHint({ slug, pools, isOwner, fromScratch }: {
  slug: string
  pools: Pool[] | undefined
  isOwner: boolean
  fromScratch: boolean
}) {
  const { t } = useTranslation()
  if (!isOwner || pools === undefined) return null
  const untouched = pools.length > 0 && pools.every((pool) => pool.qty === 0 && pool.total_value === 0)

  if (fromScratch) {
    if (pools.length > 0 && !untouched) return null
    const noPools = pools.length === 0

    return (
      <Card>
        <CardContent className="flex flex-col items-start gap-3 py-4">
          <p className="text-sm text-muted-foreground">
            {t(noPools ? 'materials.firstUseScratchNoPools' : 'materials.firstUseScratch')}
          </p>
          <Button asChild variant="outline" className="h-11">
            {noPools ? (
              <Link to="/w/$slug/materials/pools/new" params={{ slug }}>
                {t('materials.newPool')}
              </Link>
            ) : (
              <Link to="/w/$slug/materials/restocks/new" params={{ slug }}>
                {t('materials.restock')}
              </Link>
            )}
          </Button>
        </CardContent>
      </Card>
    )
  }
  if (!untouched) return null

  return (
    <Card>
      <CardContent className="flex flex-col items-start gap-3 py-4">
        <p className="text-sm text-muted-foreground">{t('materials.firstUse')}</p>
        <Button asChild variant="outline" className="h-11">
          <Link to="/w/$slug/settings/opening-balances/new" params={{ slug }}>
            {t('materials.firstUseAction')}
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}

function filterPools(pools: Pool[] | undefined, kind: PoolKind | undefined, search: string, attention: boolean): Pool[] {
  const needle = search.trim().toLocaleLowerCase('id-ID')
  return (pools ?? [])
    .filter((pool) => kind === undefined || pool.kind === kind)
    .filter((pool) => !attention || (!pool.archived_at && (pool.needs_stock_count || pool.below_minimum)))
    .filter((pool) => needle === '' || poolLabel(pool).toLocaleLowerCase('id-ID').includes(needle))
}

// The quantity on hand is the row's figure; cost per unit, total value and the minimum are the
// context line.
function PoolRow({ slug, pool }: { slug: string; pool: Pool }) {
  const { t } = useTranslation()

  return (
    <RecordListItem
      title={
        <RecordLink to="/w/$slug/materials/pools/$poolId" params={{ slug, poolId: String(pool.id) }}>
          {poolLabel(pool)}
        </RecordLink>
      }
      badges={<PoolFlags pool={pool} />}
      meta={[
        {
          icon: CoinsIcon,
          label: t('materials.columns.costPerUnit'),
          value:
            pool.cost_per_unit === null
              ? t('materials.noCost')
              : t('materials.list.costPerUnit', { amount: formatCostPerUnit(pool.cost_per_unit), unit: pool.unit }),
        },
        { icon: WalletIcon, label: t('materials.columns.totalValue'), value: t('materials.list.totalValue', { amount: formatMoney(pool.total_value) }) },
        {
          icon: TargetIcon,
          label: t('materials.columns.minQty'),
          value:
            pool.min_qty === null
              ? t('materials.noMinimum')
              : t('materials.list.minimum', { qty: `${formatQty(pool.min_qty)} ${pool.unit}` }),
        },
      ]}
      figure={`${formatQty(pool.qty)} ${pool.unit}`}
      actions={
        // A flagged Pool is one tap from the count that clears it (UI-SCREENS M-01, I-11).
        pool.needs_stock_count ? (
          <Button asChild variant="outline" size="sm">
            <Link to="/w/$slug/adjustments/new" params={{ slug }} search={{ location: 'materials', itemId: pool.id }}>
              {t('materials.adjustment')}
            </Link>
          </Button>
        ) : null
      }
    />
  )
}
